import * as React from "react";
import { type CSSProperties, Fragment, memo, useRef } from "react";
import { type User } from "~/domains/navigation/types/NavigationTypes";
import { userIsRegistered, userIsSalesman } from "~/domains/user/utils/roles";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { getMediasVariant, UserHeaderCircle } from "~/features/navigation/components/TopBar/HeaderCircle";
import { useTranslation, type WithTranslation } from "react-i18next";
import { MENU_POS_LEFT } from "~/common/components/Menu/MenuPositions";
import { MenuItemNavigation, MenuSpacer } from "~/common/components/Menu/MenuItem";
import { ACCOUNT_SELECTION_URL, MANAGE_USERS_PAGE, MY_ACCOUNT_ACCOUNT_SETTINGS_PAGE } from "~/utils/url/staticUrls";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { doLogout } from "~/features/auth/api/doLogout";
import { PopperTooltip } from "~/common/components/Tooltip";
import useMediaQuery, { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { entriesMenu } from "~/features/navigation/components/MyCompanyMenu";
import BulkATCPopup from "~/features/bulkAddToCart/components/BulkATCPopup";
import { sendPopupOpen } from "~/app/analytics/ga/bulkUpload";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { styled, FormControl, InputLabel, Menu, Typography } from "~/common/mui";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import EditIcon from "@mui/icons-material/Edit";

const WelcomeRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "none",
  cursor: "pointer",
  [theme.breakpoints.down("tablet")]: {
    marginRight: 0,
  },
}));

const UserTrigger = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
}));

const UserContainer = styled("div")({
  display: "flex",
  margin: 0,
  overflow: "hidden",
});

const AvatarTypography = styled("div")(({ theme }) => ({
  ...theme.typography.bodyLarge,
  [theme.breakpoints.down("desktop-s")]: {
    ...theme.typography.bodyMedium,
  },
}));

interface HeaderGreetingProps {
  user: User;
  showEdit: boolean;
}

export const UserNavigation = memo(({ user, showEdit }: HeaderGreetingProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const { val: isOpen, setTrue: openNavigation, setFalse: handleClose } = useBooleanState(false);
  const { val: isOpenBulk, setTrue: handleOpenBulk, setFalse: handleCloseBulk } = useBooleanState(false);

  const menuAnchor = menuAnchorRef.current;

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    openNavigation();
  };

  return (
    <WelcomeRoot ref={menuAnchorRef}>
      <TooltipWithArrow title={t("myAccount.title")} placement="bottom" hideTooltip={isOpen}>
        <UserTrigger onClick={handleOpen}>
          <LoggedInCircle user={user} />
          {isDesktop && (
            <UserContainer>
              {isOpen ? (
                <ExpandLessOutlinedIcon sx={{ ml: 1 }} />
              ) : (
                <ExpandMoreOutlinedIcon sx={{ ml: 1 }} />
              )}
            </UserContainer>
          )}
        </UserTrigger>
      </TooltipWithArrow>
      <RegisteredNavigation
        anchorEl={menuAnchor}
        isOpen={isOpen}
        onClose={handleClose}
        onOpenBulk={() => {
          handleOpenBulk();
          sendPopupOpen();
        }}
        user={user}
        showEdit={showEdit}
      />
      {isOpenBulk && <BulkATCPopup onClose={handleCloseBulk} />}
    </WelcomeRoot>
  );
});

const LoggedInCircle = React.forwardRef<HTMLDivElement, { user: User }>(
  ({ user }, ref) => (
    <AvatarTypography>
      <UserHeaderCircle variant={getMediasVariant()} ref={ref}>
        {firstLetter(user.firstName) + firstLetter(user.lastName)}
      </UserHeaderCircle>
    </AvatarTypography>
  ),
);

function firstLetter(text: string) {
  return text ? text.substring(0, 1).toUpperCase() : "";
}

const RegisteredNavigation = ({
  anchorEl,
  onClose,
  isOpen,
  user,
  showEdit,
  onOpenBulk,
}: {
  anchorEl: Element | null;
  onClose: () => void;
  isOpen: boolean;
  user: User;
  showEdit: boolean;
  onOpenBulk: () => void;
}) => {
  const { t } = useTranslation();

  const { displayUid, displayName, onlyName, displayAccount, displayEndCustomerAccount, displayEndCustomerUid } =
    extractDisplayLabels(user, t);
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const visibleMenuEntries = entriesMenu.filter((item) => item.permisisonCheck());

  return (
    <Fragment>
      <Menu open={isOpen} anchorEl={anchorEl} {...MENU_POS_LEFT} onClose={onClose} disableAutoFocusItem>
        <MenuItemNavigation>
          <Typography variant="bodyLarge" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
            {displayName}
          </Typography>
        </MenuItemNavigation>
        <MenuSpacer fullwidth />

        {!!displayAccount && !!displayUid && (
          <AccountMenuItem
            displayAccount={displayAccount}
            displayUid={displayUid}
            label={"header.selectedAccount"}
            dataTestId={"header-selected-account"}
            onCloseMenu={onClose}
            showEdit={showEdit}
          />
        )}

        {!!displayEndCustomerAccount && !!displayEndCustomerUid && (
          <AccountMenuItem
            displayAccount={displayEndCustomerAccount}
            displayUid={displayEndCustomerUid}
            label={"header.selectedAccountEndCustomer"}
            dataTestId={"header-selected-end-customer"}
            onCloseMenu={onClose}
            showEdit={showEdit}
          />
        )}
        {!isMinTablet && (
          <>
            {visibleMenuEntries.map((item) => item.factory(t, onClose, onOpenBulk))}
            <MenuSpacer fullwidth />
          </>
        )}
        {userHasPermission(Permission.USER_MANAGEMENT) && (
          <a data-test-id={"header-btn-manage-users"} href={MANAGE_USERS_PAGE} onClick={onClose}>
            <MenuItemNavigation onClick={onClose}>{t("header.link.userAccount")}</MenuItemNavigation>
          </a>
        )}
        <a data-test-id={"register-account-my-account"} href={MY_ACCOUNT_ACCOUNT_SETTINGS_PAGE}>
          <MenuItemNavigation onClick={onClose}>{t("header.link.accountSettings")}</MenuItemNavigation>
        </a>
        <a data-test-id={"register-account-logout"} onClick={() => doLogout()}>
          <MenuItemNavigation onClick={onClose}>{t("header.link.logout")}</MenuItemNavigation>
        </a>
      </Menu>
    </Fragment>
  );
};

const AccountWidth: CSSProperties = { maxWidth: "164px" };
const AccountMenuItem = ({
  displayAccount,
  displayUid,
  label,
  dataTestId,
  onCloseMenu,
  showEdit,
}: {
  displayAccount: string;
  displayUid: string;
  label: string;
  dataTestId: string;
  onCloseMenu: () => void;
  showEdit?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <MenuItemNavigation
        // @ts-ignore
        component={"a"}
        href={ACCOUNT_SELECTION_URL}
        onClick={onCloseMenu}
        className={"d-flex flex-direction-column flex-align-start"}
        data-test-id={dataTestId}
      >
        <FormControl sx={{ width: "100%", minWidth: 0 }}>
          <InputLabel shrink={true} variant="standard" sx={{ color: "text.label" }}>
            {t(label)}
          </InputLabel>
          <div className={"d-flex MuiInput-formControl pt-2 flex-align-center"}>
            <div className={"d-flex flex-direction-column"} style={{ minWidth: 0 }}>
              <PopperTooltip tooltipContent={displayAccount} placement="bottom-end">
                <div className={"d-flex"}>
                  <span className={"scha__ellipsis-text"} style={AccountWidth}>
                    {displayAccount}
                  </span>
                </div>
              </PopperTooltip>
              {displayUid && <p className={"mt-1"}>{displayUid}</p>}
            </div>
            {showEdit && <EditIcon sx={{ ml: 1, flexShrink: 0, fontSize: 16 }} />}
          </div>
        </FormControl>
      </MenuItemNavigation>
      <MenuSpacer fullwidth />
    </Fragment>
  );
};

export const extractDisplayLabels = (user: User, t: WithTranslation["t"]) => {
  const isSalesman = userIsSalesman();
  const isRegistered = userIsRegistered();
  let name, displayUid;
  const makeName = ({ firstName, lastName }: User) => {
    return [firstName, lastName].filter(Boolean).join(" ") || "MISSING NAME";
  };

  if (isSalesman) {
    const isPersonal = !user.selectedAccount || !user.selectedAccount.displayUid;
    name = isPersonal ? t("header.salesman.personal") : user.selectedAccount!.name;
    displayUid = isPersonal ? "" : user.selectedAccount!.displayUid;
  } else if (isRegistered) {
    name = makeName(user);
    displayUid = "";
  } else {
    name = makeName(user);
    displayUid = user.selectedAccount?.displayUid ?? "MISSING UID";
  }

  const displayAccount = user.selectedAccount?.name ?? "MISSING ACCOUNT";
  const displayEndCustomerAccount = user.selectedAccount?.endCustomerName ?? "";
  const displayEndCustomerUid = user.selectedAccount?.endCustomerCode ?? "";

  return {
    displayUid,
    displayName: name,
    onlyName: isRegistered,
    displayAccount,
    displayEndCustomerAccount,
    displayEndCustomerUid,
  };
};
